import { createReducer, on } from '@ngrx/store';
import { eq } from 'lodash';
import {
  loadMyReportsList,
  loadMyReportsListSuccess,
  loadMyReportsListError,
  addMyReport,
  addMyReportSuccess,
  addMyReportError,
  selectMyReport,
  deleteMyReport,
  deleteMyReportSuccess,
  deleteMyReportError,
  updateMyReport,
  updateMyReportSuccess,
  updateMyReportError,
  resetMyReportsList,
} from 'redux/actions';

export interface MyReportsListState {
  projectId: number | null;
  reports: any[];
  loaded: boolean;
  loading: boolean;
  isAdding: boolean;
  hasAdded: boolean;
  isDeleting: boolean;
  hasDeleted: boolean;
  isUpdating: boolean;
  hasUpdated: boolean;
  selectedReport: any;
  activeReportId: number | null;
  onReset: boolean;
  errors: any;
}

const initialState: MyReportsListState = {
  projectId: null,
  reports: [],
  loaded: false,
  loading: false,
  isAdding: false,
  hasAdded: false,
  isDeleting: false,
  hasDeleted: false,
  isUpdating: false,
  hasUpdated: false,
  selectedReport: null,
  activeReportId: null,
  onReset: false,
  errors: null,
};

const _myReportsListReducer = createReducer(
  initialState,

  on(loadMyReportsList, (state, { projectId }) => ({
    ...state,
    projectId,
    reports: [],
    loaded: false,
    loading: true,
    hasAdded: false,
    hasDeleted: false,
    hasUpdated: false,
    activeReportId: null,
    selectedReport: null,
    errors: null,
  })),

  on(loadMyReportsListSuccess, (state, { projectId, reports }) =>
    projectId === state.projectId
      ? {
          ...state,
          reports,
          loaded: true,
          loading: false,
        }
      : state
  ),

  on(loadMyReportsListError, (state, { projectId, payload }) =>
    projectId === state.projectId
      ? {
          ...state,
          loaded: true,
          loading: false,
          errors: {
            url: payload?.url,
            status: payload?.status,
            message: payload?.message,
          },
        }
      : state
  ),

  on(addMyReport, (state) => ({
    ...state,
    isAdding: true,
    hasAdded: false,
    errors: null,
  })),

  on(addMyReportSuccess, (state, { report }) => ({
    ...state,
    reports: [...state.reports, report],
    isAdding: false,
    hasAdded: true,
  })),

  on(addMyReportError, (state, { payload }) => ({
    ...state,
    isAdding: false,
    hasAdded: false,
    errors: {
      url: payload?.url,
      status: payload?.status,
      message: payload?.message,
    },
  })),

  on(selectMyReport, (state, { report }) => ({
    ...state,
    selectedReport: report,
    activeReportId: report?.id ?? null,
  })),

  on(deleteMyReport, (state) => ({
    ...state,
    isDeleting: true,
    hasDeleted: false,
    errors: null,
  })),

  on(deleteMyReportSuccess, (state, { reportId }) => ({
    ...state,
    reports: state.reports.filter((r) => !eq(r.id, reportId)),
    isDeleting: false,
    hasDeleted: true,
    activeReportId: eq(state.activeReportId, reportId) ? null : state.activeReportId,
    selectedReport: eq(state.activeReportId, reportId) ? null : state.selectedReport,
  })),

  on(deleteMyReportError, (state, { payload }) => ({
    ...state,
    isDeleting: false,
    hasDeleted: false,
    errors: {
      url: payload?.url,
      status: payload?.status,
      message: payload?.message,
    },
  })),

  on(updateMyReport, (state) => ({
    ...state,
    isUpdating: true,
    hasUpdated: false,
    errors: null,
  })),

  on(updateMyReportSuccess, (state, { report }) => ({
    ...state,
    reports: state.reports.map((r) => (r.id === report.id ? report : r)),
    isUpdating: false,
    hasUpdated: true,
  })),

  on(updateMyReportError, (state, { payload }) => ({
    ...state,
    isUpdating: false,
    hasUpdated: false,
    errors: {
      url: payload?.url,
      status: payload?.status,
      message: payload?.message,
    },
  })),

  on(resetMyReportsList, (state, { onReset }) => ({
    ...state,
    onReset,
    selectedReport: onReset ? null : state.selectedReport,
    activeReportId: onReset ? null : state.activeReportId,
  }))
);

export function myReportsListReducer(state, action) {
  return _myReportsListReducer(state, action);
}
